import { useCallback, useEffect, useState } from 'react';
import { Badge, Banner, BlockStack, Button, DataTable, InlineStack, Link, Tabs, Text } from '@shopify/polaris';
import axios from 'axios';
import agent from '../../app/api/agent';
import { LegacyStoreInventory } from '../../app/models/Admin';
import LegacyGarageMigrationPanel from './LegacyGarageMigrationPanel';
import LegacyBackendPilotPanel from './LegacyBackendPilotPanel';
import LegacyTargetPreparationPanel from './LegacyTargetPreparationPanel';
import StoreReviewPanel from './StoreReviewPanel';
import PilotOperatorTools from './PilotOperatorTools';
import { formatLocalDateTime } from './utils/dateTime';
import { hasMappingConflict, mappingLabel, storeDomainUrl, subscriptionLabel } from './utils/migrationPresentation';
import LegacyPreparationDiagnostics from './LegacyPreparationDiagnostics';
import MigrationDetails from './MigrationDetails';

type Props = { sourceShopId?: number; shopId?: number; token: string; onUnauthorized: () => void };

export default function LegacyDataPanel({ sourceShopId, shopId, token, onUnauthorized }: Props) {
  const [source, setSource] = useState<LegacyStoreInventory | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [pilotRefresh, setPilotRefresh] = useState(0);
  const prepared = useCallback(() => { setPilotRefresh(value => value + 1); }, []);
  const [error, setError] = useState<string | null>(null);
  const fail = useCallback((e: unknown) => {
    if (axios.isAxiosError(e) && e.response?.status === 401) onUnauthorized();
    else setError(axios.isAxiosError(e) && typeof e.response?.data?.message === 'string'
      ? e.response.data.message : 'Unable to load legacy data.');
  }, [onUnauthorized]);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let id = sourceShopId;
      if (!id && shopId) {
        const directory = await agent.Admin.migrationStores(token);
        id = directory.items.find(x => x.shopId === shopId)?.sourceShopId;
      }
      if (id) {
        try { setSource(await agent.Admin.legacyStore(token, id)); }
        catch (e) { if (!axios.isAxiosError(e) || e.response?.status !== 404) throw e; setSource(null); }
      } else setSource(null);
    } catch (e) { fail(e); }
    finally { setLoading(false); }
  }, [sourceShopId, shopId, token, fail]);
  useEffect(() => { setSource(null); setSelectedTab(0); void load(); }, [load]);
  const verify = async () => {
    if (!source) return;
    setBusy(true); setError(null);
    try { setSource(await agent.Admin.verifyLegacySubscription(token, source.sourceShopId)); }
    catch (e) { fail(e); } finally { setBusy(false); }
  };
  const blockedReason = !source ? undefined : source.missingFromSource
    ? 'This source store is no longer present in the PFV1 database. Resolve its identity before starting a run.'
    : hasMappingConflict(source.mappingStatus) ? 'Source identity needs review. Conflicting or rehearsal mappings are never reassigned automatically.'
    : !source.hasOfflineToken ? 'PFV1 has no offline token for this source. Shopify customer access is required before a scan can start.'
    : source.sourceStatus?.toLowerCase() !== 'active' ? 'PFV1 marks this source inactive. Review its installation and access before starting a scan.' : undefined;
  const domainUrl = storeDomainUrl(source?.storefrontDomain || source?.myShopifyDomain);
  return <BlockStack gap="400">
    <InlineStack align="space-between" blockAlign="center">
      <BlockStack gap="200"><Text as="h2" variant="headingLg">{source?.name || source?.myShopifyDomain || 'Legacy data'}</Text>
        {source ? <InlineStack gap="200"><Badge>PFV1</Badge><Text as="span" tone="subdued">#{source.sourceShopId}</Text>
          {domainUrl ? <Link url={domainUrl} target="_blank">{source.storefrontDomain || source.myShopifyDomain}</Link> : null}
          <Badge tone={source.subscriptionStatus === 'Active' ? 'success' : undefined}>{subscriptionLabel(source.subscriptionStatus)}</Badge></InlineStack> : null}
      </BlockStack>
      <Button onClick={() => void load()} loading={loading} disabled={busy}>Reload legacy data</Button>
    </InlineStack>
    {error ? <Banner tone="critical">{error}</Banner> : null}
    {!loading && !source ? <Banner tone="info">No PFV1 inventory record is linked yet. Refresh from PFV1 on the <Link url="/pf-console/stores">stores list</Link>, then reload this store.</Banner> : null}
    {source ? <>
      <Tabs tabs={[
        { id: 'overview', content: 'Overview' }, { id: 'migration', content: 'Migration' }, { id: 'diagnostics', content: 'Access & diagnostics' },
      ]} selected={selectedTab} onSelect={setSelectedTab} />
      <div hidden={selectedTab !== 0}>
        <BlockStack gap="400">
          <StoreReviewPanel key={source.sourceShopId} id={source.sourceShopId} legacySource token={token} onUnauthorized={onUnauthorized} />
          <PilotOperatorTools key={`audit-${source.sourceShopId}`} sourceId={source.sourceShopId} token={token} active={false} domain={source.myShopifyDomain || undefined} />
        </BlockStack>
      </div>
      {selectedTab === 1 ? <BlockStack gap="500">
        <LegacyBackendPilotPanel key={`${source.sourceShopId}:${pilotRefresh}`} sourceShopId={source.sourceShopId} token={token} onUnauthorized={onUnauthorized} />
        <BlockStack gap="0">
          <MigrationDetails title="Target preparation" open={source.mappingStatus !== 'Mapped'}>
            <LegacyTargetPreparationPanel sourceShopId={source.sourceShopId} domain={source.myShopifyDomain || ''}
              token={token} onUnauthorized={onUnauthorized} onCompleted={prepared} />
          </MigrationDetails>
          <MigrationDetails title="Garage tools & run history">
            <LegacyGarageMigrationPanel key={source.sourceShopId} sourceShopId={source.sourceShopId}
              blockedReason={blockedReason} token={token} onUnauthorized={onUnauthorized} />
          </MigrationDetails>
        </BlockStack>
      </BlockStack> : null}
      {selectedTab === 2 ? <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center"><Text as="h3" variant="headingMd">Source identity & access</Text>
          <Button onClick={() => void verify()} loading={busy} disabled={loading || !source.myShopifyDomain || source.missingFromSource || !source.hasOfflineToken}>Verify PFV1 subscription</Button></InlineStack>
        <DataTable columnContentTypes={['text', 'text']} headings={['Field', 'Value']} rows={[
          ['Shopify domain', source.myShopifyDomain || 'Unverified'], ['Email', source.email || '—'],
          ['Mapping', mappingLabel(source.mappingStatus)], ['PFV1 database status', source.sourceStatus || 'Unknown'],
          ['PFV1 offline token', source.hasOfflineToken ? 'Present' : 'Missing'],
          ['First recorded install (PFV1)', source.sourceCreatedOn?.slice(0, 10) || 'Not recorded'],
          ['Last app access', formatLocalDateTime(source.sourceLastAccessAt)],
          ['Inventory refreshed', formatLocalDateTime(source.refreshedAt)],
          ['Subscription last checked', formatLocalDateTime(source.subscriptionCheckedAt)],
        ]} />
        <Text as="p" tone="subdued">First recorded install is the creation date of the retained PFV1 store record. Reauthorizing an existing record does not reset it.</Text>
        <LegacyPreparationDiagnostics source={source} />
      </BlockStack> : null}
    </> : null}
  </BlockStack>;
}
